import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { BehaviorSubject, catchError, map, Observable, of } from "rxjs";
import Swal from "sweetalert2";
import { ApiService } from "../services/api.service";
import { GigsumoConstants } from "../services/GigsumoConstants";
import { BusinessProvider } from "./business-details-guard.ts.service";

// Subject for contract module to subscribe to business data
export let contractBusinessSubject = new BehaviorSubject<any>(null);

/**
 * Injectable provider to store and retrieve contract business context
 * This allows components within the contract module to access business details
 * without repeatedly querying localStorage or making API calls
 */
@Injectable({
  providedIn: "root"
})
export class ContractBusinessProvider {

  private businessDetails: any = null;

  setBusinessDetails(details: any): void {
    this.businessDetails = details;
    contractBusinessSubject.next(details);
  }

  getBusinessDetails(): any {
    return this.businessDetails;
  }

  getBusinessId(): string | null {
    return this.businessDetails?.businessId || this.businessDetails?.id || null;
  }

  getBusinessName(): string | null {
    return this.businessDetails?.businessName || null;
  }

  getGsBid(): string | null {
    return this.businessDetails?.bid || null;
  }

  getOrganisationId(): string | null {
    return this.businessDetails?.organisationId || null;
  }

  getRole(): string | null {
    return this.businessDetails?.role || null;
  }

  isSuperAdmin(): boolean {
    return this.businessDetails?.role === "SUPERADMIN";
  }

  isAdmin(): boolean {
    const role = this.businessDetails?.role;
    return role === "SUPERADMIN" || role === "ADMIN";
  }

  clear(): void {
    this.businessDetails = null;
    contractBusinessSubject.next(null);
  }
}

/**
 * Resolver for the Contract Center module
 * Fetches and provides business details to the contract module routes
 * Similar to adminResolver but specifically for contract module context
 */
export const contractResolver: ResolveFn<any> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const contractProvider = inject(ContractBusinessProvider);
  const busProvider = inject(BusinessProvider);
  const params = route.queryParams;

  // First check if business details are already available from BusinessProvider
  const existingBusiness = busProvider.getBusiness();

  if (existingBusiness != null) {
    const contractData = {
      ...existingBusiness,
      businessUser: true,
      isSuperAdmin: existingBusiness.role === "SUPERADMIN",
      isAdmin: existingBusiness.role === "SUPERADMIN" || existingBusiness.role === "ADMIN"
    };
    contractProvider.setBusinessDetails(contractData);
    return of(contractData);
  } else {
    // If no business data, return params (similar to admin resolver fallback)
    return of(params);
  }
};

/**
 * Guard for the Contract Center module
 * Ensures user has a valid business context before accessing contract module
 * Only users with Business/Enterprise plans can access the contract center
 */
export const contractGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const userId = localStorage.getItem('userId');
  const contractProvider = inject(ContractBusinessProvider);
  const busProvider = inject(BusinessProvider);
  const router = inject(Router);
  const API = inject(ApiService);

  // Check if business details are already loaded
  const existingBusiness = busProvider.getBusiness();

  if (existingBusiness != null) {
    // Business already loaded, set contract provider and allow access
    const contractData = {
      ...existingBusiness,
      businessUser: true,
      isSuperAdmin: existingBusiness.role === "SUPERADMIN",
      isAdmin: existingBusiness.role === "SUPERADMIN" || existingBusiness.role === "ADMIN"
    };
    contractProvider.setBusinessDetails(contractData);
    const myBusinessId = localStorage.getItem('myBusinessId') || '';
    if(myBusinessId){
      return true;
    }
  }

  // Fetch business details if not already loaded
  return getBusinessDetails(userId, API).pipe(
    map(response => {
      if (response.code === GigsumoConstants.SUCESSCODE &&
        response.data.businessData && (
          response.data.businessData.subscriptionType === GigsumoConstants.StarterPlan ||
          response.data.businessData.subscriptionType === GigsumoConstants.GrowthPlan ||
          response.data.businessData.subscriptionType === GigsumoConstants.ScaleXPlan
        )) {

        const data = response.data.businessData;
        const businessDetails = {
          role: response.data.role,
          id: data.businessId,
          businessId: data.businessId,
          businessEmail: data.businessEmail,
          businessName: data.businessName,
          bid: data.platformBusinesId,
          plan: data.subscriptionType,
          businessOwner: data.businessOwner,
          organisationId: data.organisationId,
          businessUser: true,
          isSuperAdmin: response.data.role === "SUPERADMIN",
          isAdmin: response.data.role === "SUPERADMIN" || response.data.role === "ADMIN"
        };

        // Set in both providers for consistency
        busProvider.setBusiness(businessDetails);
        contractProvider.setBusinessDetails(businessDetails);
        localStorage.setItem('myBusinessId', data.businessId || '');
        return true;
      } else {
        // User doesn't have a valid business subscription
        contractProvider.clear();
        showAccessDeniedMessage();
        router.navigate(['landingPage']);
        return false;
      }
    }),
    catchError(error => {
      console.error('Contract guard error:', error);
      showErrorMessage();
      router.navigate(['landingPage']);
      return of(false);
    })
  );
};

/**
 * Fetch business details from API
 */
function getBusinessDetails(userId: string, API: ApiService): Observable<any> {
  return API.query(`business/findUserRoleInBusiness/${userId}`).pipe(
    catchError(error => {
      console.error('Error fetching business details:', error);
      return of({ code: 'ERROR', data: null });
    })
  );
}

/**
 * Show access denied message
 */
function showAccessDeniedMessage(): void {
  Swal.fire({
    title: "Access Denied",
    icon: "warning",
    text: "Contract Center is available for Business users only. Please upgrade your plan to access this feature.",
  });
}

/**
 * Show error message
 */
function showErrorMessage(): void {
  Swal.fire({
    title: "Something went wrong!",
    icon: "error",
    text: "Please try again later",
    timer: 2000
  });
}
